import { memo, useEffect, useRef, useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { NavItem } from '@/types/whoami';
import { cn } from '@/utils/classnames';

import useWxNavbar from './use-wx-navbar';

// An empty column is worse than a late shift if the bundle never paints.
export const PLACEHOLDER_TIMEOUT_MS = 15000;

const PRIMARY_LABEL_WIDTHS = ['30%', '40%', '55%', '35%', '45%'];
const SECONDARY_LABEL_WIDTHS = [85, 110, 75];

// Collapsed, or below the bundle's 640px breakpoint, the navbar shows icons
// only.
const LABEL = 'group-data-[collapsed]:hidden max-sm:hidden';

export type WxNavbarHostProps = {
  userId?: string;
  locale?: string;
  companyId?: string;
  inboxCount?: number;
  employerDomain?: string;
  employerBasePath?: string;
  disableNavigation?: boolean;
  mainConfig?: NavItem[];
  secondaryConfig?: NavItem[];
  dropdownConfig?: NavItem[];
  /** Host SPA path so the UMD can resolve active nav (not in the same React root as the router). */
  hostPathname: string;
};

/**
 * Mirrors the bundle's WxNavbarSkeleton, so the handoff to it does not
 * flicker. Widths: 264px expanded, 48px collapsed and below 640px.
 */
function NavbarPlaceholder({ isCollapsed }: { isCollapsed: boolean }) {
  return (
    <div
      aria-hidden="true"
      data-collapsed={isCollapsed ? '' : undefined}
      data-testid="wx-navbar-placeholder"
      className="group flex min-h-0 w-[264px] flex-col overflow-hidden bg-white shadow-md data-[collapsed]:w-12 max-sm:w-12"
    >
      <div className="flex min-h-14 items-center px-4">
        <Skeleton className={cn('h-[18px] w-[117px] shrink-0', LABEL)} />
      </div>
      <div className="flex-1 px-1 py-2">
        {PRIMARY_LABEL_WIDTHS.map(width => (
          <div key={width} className="flex h-10 items-center gap-2 p-1">
            <Skeleton className="size-8 shrink-0" />
            <Skeleton className={cn('h-5 shrink-0', LABEL)} style={{ width }} />
          </div>
        ))}
      </div>
      {SECONDARY_LABEL_WIDTHS.map(width => (
        <div key={width} className="flex h-8 items-center gap-2 px-4 py-1.5">
          <Skeleton className="size-4 shrink-0 rounded-sm" />
          <Skeleton className={cn('h-5 shrink-0', LABEL)} style={{ width }} />
        </div>
      ))}
      <div className="border-fx-neutral-200 flex items-center gap-2 border-t p-2">
        <Skeleton className="size-8 shrink-0 rounded-full" />
        <Skeleton className={cn('h-5 w-4/5 shrink-0', LABEL)} />
      </div>
    </div>
  );
}

// Same storage key as the bundle's useCollapsedState.
function readCollapsed(userId?: string) {
  try {
    return localStorage.getItem(`wx-navbar:${userId}:collapsed`) === '1';
  } catch {
    return false;
  }
}

/**
 * Wraps the wx-navbar UMD bundle. Memoized so unrelated parent re-renders that
 * pass referentially-stable props skip straight through (the new UMD applies
 * subsequent `render()` calls in place, so we want to avoid unnecessary calls
 * but tolerate the ones that do come through).
 *
 * `hostPathname` must be the host SPA's current path (typically
 * `useLocation().pathname`) so the navbar can resolve active state without
 * sharing a router context with the host.
 */
function WxNavbarHost(props: WxNavbarHostProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isPainted, setIsPainted] = useState(false);
  const [isCollapsed] = useState(() => readCollapsed(props.userId));

  // forward all props 1:1 to the UMD render via the hook
  const { isUnavailable } = useWxNavbar({ mountRef: ref, renderProps: props });
  const showPlaceholder = !isPainted && !isUnavailable;

  // The bundle mounts into a shadow root and paints nothing until LaunchDarkly
  // resolves, so the mount's own width is the only signal that the sidebar (or
  // its skeleton) is on screen.
  useEffect(() => {
    const el = ref.current;
    if (!showPlaceholder || !el) return undefined;
    const timeout = setTimeout(
      () => setIsPainted(true),
      PLACEHOLDER_TIMEOUT_MS,
    );
    if (typeof ResizeObserver === 'undefined')
      return () => clearTimeout(timeout);
    const observer = new ResizeObserver(entries => {
      if ((entries[0]?.contentRect.width ?? 0) > 0) setIsPainted(true);
    });
    observer.observe(el);
    return () => {
      clearTimeout(timeout);
      observer.disconnect();
    };
  }, [showPlaceholder]);

  // One grid cell holds both, so the slot is as wide as the wider of the two
  // and never doubles while one replaces the other. `justify-self-start` keeps
  // the mount from reporting the placeholder's width before the navbar paints.
  return (
    <div className="grid h-full min-h-0 grid-cols-[auto] grid-rows-[minmax(0,1fr)] *:justify-self-start *:[grid-area:1/1]">
      {showPlaceholder && <NavbarPlaceholder isCollapsed={isCollapsed} />}
      <div id="wx-navbar-root" ref={ref} />
    </div>
  );
}

export default memo(WxNavbarHost);
